import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, IconButton, Stack } from "@mui/material";
import { ColorsConstants } from "../../cssUtils/constants";
import CustomTabs from "../../common/custom-tabs";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";
import Filter from "../Filter";
import LeadFormDialog from "./LeadFormDialog";
import LeadDetailView from "./LeadDetailView";
import { Edit, Add } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import { useAuth } from "../../hooks/AuthProvider";
import {
  MFD_LEADS_ENDPOINT,
  MFD_REGISTERED_LEADS_ENDPOINT,
} from "../../config/urls";

const ALL_TABS = [
  { value: "leads",      label: "Leads",            permission: "mfd_leads.view" },
  { value: "registered", label: "Registered Leads", permission: "mfd_registered_leads.view" },
];

const LEAD_INTEREST_OPTIONS = [
  "Review My Investments", "Product Offerings", "One-Time Investment",
  "New SIP", "Financial Calculators", "Meeting / Conversation",
];
const REG_INTEREST_OPTIONS = ["New Investment", "Goal Planning", "New Product Offering"];
const LEAD_STATUS_OPTIONS  = ["Open", "In Progress", "Converted", "Not Interested"];

const LEADS_FILTER_FIELDS = [
  { label: "Interest", type: "single_select_list", values: LEAD_INTEREST_OPTIONS },
  { label: "Status",   type: "single_select_list", values: LEAD_STATUS_OPTIONS },
];
const REG_FILTER_FIELDS = [
  { label: "Interest", type: "single_select_list", values: REG_INTEREST_OPTIONS },
  { label: "Status",   type: "single_select_list", values: LEAD_STATUS_OPTIONS },
];

const STATUS_CHIP_COLOR = {
  Open:             "info",
  "In Progress":    "warning",
  Converted:        "success",
  "Not Interested": "default",
};

const StatusCell = ({ row }) => (
  <Chip
    variant="outlined"
    size="small"
    label={row.status}
    color={STATUS_CHIP_COLOR[row.status] ?? "default"}
  />
);

// ── Normalise API → UI field names ────────────────────────────────────────────

const normalizeLead = (l) => ({
  ...l,
  client_name:  l.name,
  phone_number: l.phone_no,
});

// ── Column definitions ────────────────────────────────────────────────────────

const getLeadColumns = (onEdit) => [
  { id: "client_name",  label: "Name",         minWidth: 160 },
  { id: "country_code", label: "Country Code", minWidth: 110 },
  { id: "phone_number", label: "Phone Number",  minWidth: 140 },
  { id: "email",        label: "Email",         minWidth: 210 },
  { id: "interest",     label: "Interest",      minWidth: 180 },
  {
    id: "status",
    label: "Status",
    minWidth: 150,
    render: (row) => <StatusCell row={row} />,
  },
  { id: "comment", label: "Comment", minWidth: 260 },
  {
    id: "actions",
    label: "Actions",
    minWidth: 80,
    render: (row) => (
      <Stack direction="row">
        <IconButton size="medium" sx={{ color: ColorsConstants.primaryBlue100 }} onClick={() => onEdit(row)}>
          <Edit fontSize="medium" />
        </IconButton>
      </Stack>
    ),
  },
];

const getRegisteredColumns = (onEdit) => [
  { id: "pan",          label: "PAN",          minWidth: 130 },
  { id: "client_code",  label: "Client Code",  minWidth: 120 },
  { id: "client_name",  label: "Name",         minWidth: 160 },
  { id: "country_code", label: "Country Code", minWidth: 110 },
  { id: "phone_number", label: "Phone Number", minWidth: 140 },
  { id: "email",        label: "Email",        minWidth: 210 },
  { id: "interest",     label: "Interest",     minWidth: 180 },
  {
    id: "status",
    label: "Status",
    minWidth: 150,
    render: (row) => <StatusCell row={row} />,
  },
  { id: "comment", label: "Comment", minWidth: 260 },
  {
    id: "actions",
    label: "Actions",
    minWidth: 80,
    render: (row) => (
      <Stack direction="row">
        <IconButton size="medium" sx={{ color: ColorsConstants.primaryBlue100 }} onClick={() => onEdit(row)}>
          <Edit fontSize="medium" />
        </IconButton>
      </Stack>
    ),
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

const Leads = () => {
  const { getValidToken, permissions } = useAuth();

  const visibleTabs = ALL_TABS.filter(
    (tab) => !tab.permission || permissions?.includes(tab.permission)
  );

  const [activeTab, setActiveTab] = useState(visibleTabs[0]?.value ?? "leads");
  const isRegistered = activeTab === "registered";

  // Leads tab state
  const [leadsData, setLeadsData]           = useState([]);
  const [leadsTotal, setLeadsTotal]         = useState(0);
  const [leadsPage, setLeadsPage]           = useState(0);
  const [leadsRpp, setLeadsRpp]             = useState(20);
  const [leadsSearch, setLeadsSearch]       = useState("");
  const [leadsFilters, setLeadsFilters]     = useState([]);

  // Registered leads tab state
  const [regData, setRegData]               = useState([]);
  const [regTotal, setRegTotal]             = useState(0);
  const [regPage, setRegPage]               = useState(0);
  const [regRpp, setRegRpp]                 = useState(20);
  const [regSearch, setRegSearch]           = useState("");
  const [regFilters, setRegFilters]         = useState([]);

  const [loading, setLoading]               = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [view, setView]                     = useState(null);
  const [isModalOpen, setIsModalOpen]       = useState(false);
  const [editData, setEditData]             = useState(null);

  // ── Auth helpers ───────────────────────────────────────────────────────────

  const getHeaders = useCallback(async () => {
    const token  = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [getValidToken]);

  // ── Fetchers ───────────────────────────────────────────────────────────────

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: leadsRpp, offset: leadsPage * leadsRpp });
    if (leadsSearch.trim()) params.set("search", leadsSearch.trim());
    leadsFilters.forEach(({ field, operator, value }) => {
      params.append(field.toLowerCase().replaceAll(" ", "_"), `${operator.toLowerCase().replaceAll(" ", "_")}:${value ?? ""}`);
    });
    try {
      const headers = await getHeaders();
      const res = await fetch(`${MFD_LEADS_ENDPOINT}?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to fetch leads");
      const data = await res.json();
      setLeadsData((data.results ?? []).map(normalizeLead));
      setLeadsTotal(data.count ?? 0);
    } catch (err) {
      console.error("Error fetching leads:", err);
    } finally {
      setLoading(false);
    }
  }, [leadsPage, leadsRpp, leadsSearch, leadsFilters, getHeaders]);

  const fetchRegisteredLeads = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: regRpp, offset: regPage * regRpp });
    if (regSearch.trim()) params.set("search", regSearch.trim());
    regFilters.forEach(({ field, operator, value }) => {
      params.append(field.toLowerCase().replaceAll(" ", "_"), `${operator.toLowerCase().replaceAll(" ", "_")}:${value ?? ""}`);
    });
    try {
      const headers = await getHeaders();
      const res = await fetch(`${MFD_REGISTERED_LEADS_ENDPOINT}?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to fetch registered leads");
      const data = await res.json();
      setRegData((data.results ?? []).map(normalizeLead));
      setRegTotal(data.count ?? 0);
    } catch (err) {
      console.error("Error fetching registered leads:", err);
    } finally {
      setLoading(false);
    }
  }, [regPage, regRpp, regSearch, regFilters, getHeaders]);

  useEffect(() => { fetchLeads(); },            [fetchLeads]);
  useEffect(() => { fetchRegisteredLeads(); },  [fetchRegisteredLeads]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleLeadView = (row) => setView({ lead: row, isRegistered: false });
  const handleRegView  = (row) => setView({ lead: row, isRegistered: true });

  const handleEdit = (row) => { setEditData(row); setIsModalOpen(true); };
  const handleAdd  = ()    => { setEditData(null); setIsModalOpen(true); };

  const handleSave = async (form) => {
    const endpoint = isRegistered ? MFD_REGISTERED_LEADS_ENDPOINT : MFD_LEADS_ENDPOINT;
    const url    = editData ? `${endpoint}${editData.id}/` : endpoint;
    const method = editData ? "PATCH" : "POST";
    const body = {
      interest: form.interest,
      status:   form.status,
      comment:  form.comment,
      ...(isRegistered
        ? form.client_id != null && { client: form.client_id }
        : {
            name:         form.client_name,
            country_code: form.country_code,
            phone_no:     form.phone_number,
            email:        form.email,
          }),
    };
    setSaving(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error("Failed to save lead");
      setIsModalOpen(false);
      isRegistered ? fetchRegisteredLeads() : fetchLeads();
    } catch (err) {
      console.error("Error saving lead:", err);
    } finally {
      setSaving(false);
    }
  };

  // ── Tab-aware derived state ────────────────────────────────────────────────

  const rows        = isRegistered ? regData       : leadsData;
  const totalCount  = isRegistered ? regTotal      : leadsTotal;
  const page        = isRegistered ? regPage       : leadsPage;
  const rpp         = isRegistered ? regRpp        : leadsRpp;
  const search      = isRegistered ? regSearch     : leadsSearch;
  const filters     = isRegistered ? regFilters    : leadsFilters;
  const filterFields = isRegistered ? REG_FILTER_FIELDS : LEADS_FILTER_FIELDS;

  const handlePageChange = (_, newPage) => {
    isRegistered ? setRegPage(newPage) : setLeadsPage(newPage);
  };
  const handleRppChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (isRegistered) { setRegRpp(val); setRegPage(0); }
    else              { setLeadsRpp(val); setLeadsPage(0); }
  };
  const handleSearchChange = (e) => {
    const val = e.target.value;
    if (isRegistered) { setRegSearch(val); setRegPage(0); }
    else              { setLeadsSearch(val); setLeadsPage(0); }
  };

  const handleFilterApply = (newFilters) => {
    if (isRegistered) { setRegFilters(newFilters); setRegPage(0); }
    else              { setLeadsFilters(newFilters); setLeadsPage(0); }
  };

  const columns = useMemo(() => {
    return isRegistered
      ? getRegisteredColumns(handleEdit)
      : getLeadColumns(handleEdit);
  }, [isRegistered]);

  const handleRowClick = (row) => (isRegistered ? handleRegView(row) : handleLeadView(row));

  // ── Render ─────────────────────────────────────────────────────────────────

  if (view) {
    return (
      <LeadDetailView
        lead={view.lead}
        isRegistered={view.isRegistered}
        onBack={() => setView(null)}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <CustomTabs
           value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          tabs={visibleTabs}
          sx={{ maxWidth: 800 }} 
        />
        <ButtonOne startIcon={<Add />} onClick={handleAdd}>
          Add {isRegistered ? "Registered Lead" : "Lead"}
        </ButtonOne>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2,  alignItems: { xs: "stretch", sm: "center" }, flexDirection: { xs: "column", sm: "row" },}}>
        <Box sx={{ flex: 1, width: "100%", maxWidth: { xs: "100%", md: 400, lg: 450 },}}>
          <TableSearchInput
            searchQuery={search}
            onSearchChange={handleSearchChange}
            placeholder={
              isRegistered
              ? "Search by name, phone number, email, or interest"
              : "Search by name, phone number, email, or interest"
            }

          />
        </Box>
        <Filter
          fields={filterFields}
          onApply={handleFilterApply}
          initalFilters={filters}
        />
      </Box>

      <AppTable
        columns={columns}
        rows={rows}
        rowKey="id"
        loading={loading}
        emptyMessage="No leads found"
        maxHeight={600}
        hideMainScroll={false}
        containerSx={{ overflowX: "auto" }}
        count={totalCount}
        page={page}
        rowsPerPage={rpp}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRppChange}
        onRowClick={handleRowClick}
      />

      <LeadFormDialog
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        editData={editData}
        type={activeTab}
        saving={saving}
      />
    </Box>
  );
};

export default Leads;
